import { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Coins, Save, ChevronDown, ChevronUp, Filter, RefreshCw, ShieldCheck,
} from "lucide-react";

/**
 * /dashboard/admin/diploks/feature-costs — super_admin only.
 *
 * Admin-configurable Diploks cost rules.  Loaded from the internal
 * admin endpoint that merges DB rows with default seed rules so every
 * known feature is editable without re-seeding.
 */
export default function FeatureCostRulesAdminPage() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("");
  const [draft, setDraft] = useState({});

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/admin/diploks/feature-cost-rules");
      const list = r.data?.rules || [];
      setRows(list);
      const d = {};
      list.forEach((x) => {
        d[x.feature_key] = {
          cost: x.cost,
          active: x.active !== false,
          prefer_payer: x.prefer_payer || "personal",
        };
      });
      setDraft(d);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    if (!filter.trim()) return rows;
    const q = filter.toLowerCase().trim();
    return rows.filter((r) =>
      r.feature_key.toLowerCase().includes(q) ||
      (r.label || "").toLowerCase().includes(q) ||
      (r.category || "").toLowerCase().includes(q)
    );
  }, [rows, filter]);

  const save = async (row) => {
    const d = draft[row.feature_key];
    if (!d) return;
    setBusy(row.feature_key);
    try {
      await api.patch(
        `/admin/diploks/feature-cost-rules/${encodeURIComponent(row.feature_key)}`,
        { cost: Number(d.cost), active: d.active, prefer_payer: d.prefer_payer },
      );
      toast.success(`${row.label || row.feature_key} updated.`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="feature-cost-rules-admin-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Super Admin · Sprint R2
            </p>
            <h1 className="mt-2 text-3xl font-bold text-[#0A192F] tracking-tight">
              Diploks Feature Cost Rules
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-xl">
              Adjust Diploks cost per premium feature without redeploys.
              Costs are visible only to the admin and on user ledger
              entries — never as a public rate card.
            </p>
          </div>
          <button
            type="button"
            onClick={refresh}
            data-testid="feature-cost-rules-refresh"
            className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-slate-200 hover:border-slate-300"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <div className="relative flex-1">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Filter by feature key, label or category"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="feature-cost-rules-filter"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-200 text-xs"
            />
          </div>
          <p className="text-xs text-slate-500 tabular-nums">
            {filtered.length} / {rows.length}
          </p>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  Feature
                </th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  Category
                </th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  Cost
                </th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  Pay from
                </th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  Active
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((row) => {
                const d = draft[row.feature_key] || {};
                const dirty =
                  Number(d.cost) !== Number(row.cost) ||
                  d.active !== (row.active !== false) ||
                  d.prefer_payer !== (row.prefer_payer || "personal");
                return (
                  <tr
                    key={row.feature_key}
                    data-testid={`feature-cost-row-${row.feature_key}`}
                  >
                    <td className="px-4 py-3">
                      <p
                        className="font-semibold text-[#0A192F] text-sm"
                        data-testid={`feature-cost-label-${row.feature_key}`}
                      >
                        {row.label || row.feature_key}
                      </p>
                      <p className="text-[10px] text-slate-500 font-mono">
                        {row.feature_key}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-700 capitalize">
                        {row.category || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="inline-flex items-center gap-1.5">
                        <input
                          type="number"
                          min={0}
                          value={d.cost ?? row.cost ?? 0}
                          onChange={(e) =>
                            setDraft((s) => ({
                              ...s,
                              [row.feature_key]: { ...s[row.feature_key], cost: e.target.value },
                            }))
                          }
                          data-testid={`feature-cost-input-${row.feature_key}`}
                          className="w-20 px-2 py-1.5 rounded border border-slate-200 text-sm tabular-nums"
                        />
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-0.5">
                          <Coins className="h-3 w-3 text-[#D4AF37]" /> Diploks
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={d.prefer_payer || row.prefer_payer || "personal"}
                        onChange={(e) =>
                          setDraft((s) => ({
                            ...s,
                            [row.feature_key]: { ...s[row.feature_key], prefer_payer: e.target.value },
                          }))
                        }
                        data-testid={`feature-cost-payer-${row.feature_key}`}
                        className="px-2 py-1.5 rounded border border-slate-200 text-xs"
                      >
                        <option value="personal">Personal</option>
                        <option value="organisation">Organisation</option>
                        <option value="sponsored">Sponsored</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={d.active ?? (row.active !== false)}
                          onChange={(e) =>
                            setDraft((s) => ({
                              ...s,
                              [row.feature_key]: { ...s[row.feature_key], active: e.target.checked },
                            }))
                          }
                          data-testid={`feature-cost-active-${row.feature_key}`}
                        />
                        <span className="text-xs text-slate-600">
                          {d.active ?? (row.active !== false) ? "On" : "Off"}
                        </span>
                      </label>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        disabled={!dirty || busy === row.feature_key}
                        onClick={() => save(row)}
                        data-testid={`feature-cost-save-${row.feature_key}`}
                        className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider bg-[#0A192F] text-white px-3 py-1.5 rounded-md hover:bg-[#0F2C50] disabled:opacity-30"
                      >
                        <Save className="h-3 w-3" />
                        {busy === row.feature_key ? "Saving…" : "Save"}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-xs text-slate-500 py-8">
                    No cost rules match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] text-slate-700 flex items-start gap-2">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
          <p>
            Feature costs adjust the central deduction engine on the fly.
            Existing in-flight consumes are not affected — the engine
            reads the active rule at the moment of consumption. There is
            <strong> no public rate card</strong>; users see costs only on
            their personal wallet (per category) and on their ledger.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}
